// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@sc/App/Store/Reducers";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    const shipTo = getShipToState(state, cart?.shipToId)?.value;
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        shipTo,
        // XNG-Change
        useOneTimeAddress: shipTo?.oneTimeAddress,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationBillingAndShippingStyles {
    container?: GridContainerProps;
    shipToGridItem?: GridItemProps;
    shipToTitleText?: TypographyPresentationProps;
    shipToText?: TypographyPresentationProps;
    shipToAddress?: AddressInfoDisplayStyles;
    pickUpGridItem?: GridItemProps;
    pickUpTitleText?: TypographyPresentationProps;
    pickUpText?: TypographyPresentationProps;
    pickUpAddress?: AddressInfoDisplayStyles;
    billToGridItem?: GridItemProps;
    billToTitleText?: TypographyPresentationProps;
    billToText?: TypographyPresentationProps;
    // XNG-Change
    title?: TypographyPresentationProps;
    addressesWrapper?: InjectableCss;
    notesWrapper?: GridItemProps;
    notesTitle?: TypographyPresentationProps;
    notesDescription?: TypographyPresentationProps;
    addressTextStyle?: TypographyPresentationProps;
}

export const billingAndShippingStyles: OnePageCheckoutConfirmationBillingAndShippingStyles = {
    container: { gap: 20 },
    shipToGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    shipToTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    pickUpGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    pickUpTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    billToGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    billToTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = billingAndShippingStyles;

const OnePageCheckoutConfirmationBillingAndShipping = ({ cart, billTo, shipTo, useOneTimeAddress }: Props) => {
    if (!cart || !billTo || !shipTo) {
        return null;
    }

    return (
        <>
            {/* XNG-Change */}
            <Typography as="h3" {...styles.title}>
                {translate("Addresses")}
            </Typography>
            <StyledWrapper {...styles.container}>
                <StyledWrapper
                    {...styles.billToGridItem}
                    data-test-selector="onePageCheckOutConfirmationBillingInformation"
                >
                    <Typography as="h3" {...styles.billToTitleText}>
                        {/* XNG-Change */}
                        {translate("Bill-To Account")}
                    </Typography>
                    <Typography as="p" {...styles.billToText}>
                        <AddressInfoDisplay
                            {...billTo}
                            {...{ customerSequence: billTo.customerSequence || billTo.customerNumber }}
                            state={billTo.state?.abbreviation}
                            country={billTo.country?.name}
                        />
                    </Typography>
                </StyledWrapper>
                <StyledWrapper
                    {...styles.shipToGridItem}
                    data-test-selector="onePageCheckOutConfirmationShippingInformation"
                >
                    <Typography as="h3" {...styles.shipToTitleText}>
                        {/* XNG-Change */}
                        {translate("Ship-To Account")}
                    </Typography>
                    <AddressInfoDisplay
                        {...shipTo}
                        {...{
                            customerSequence: useOneTimeAddress ? "" : shipTo.customerSequence || shipTo.customerNumber,
                        }}
                        state={shipTo.state?.abbreviation}
                        country={shipTo.country?.name}
                        extendedStyles={styles.shipToAddress}
                    />
                    {useOneTimeAddress && (
                        <>
                            {shipTo?.properties?.phone2 && (
                                <Typography {...styles.addressTextStyle} variant="p">
                                    {shipTo.properties.phone2}
                                </Typography>
                            )}
                            {shipTo?.properties?.smid && (
                                <Typography {...styles.addressTextStyle} variant="p">
                                    {translate("SMID")}: {shipTo.properties.smid}
                                </Typography>
                            )}
                            {shipTo?.properties?.companyDepartmentNumber && (
                                <Typography {...styles.addressTextStyle} variant="p">
                                    {translate("Co/Dept Number")}: {shipTo.properties.companyDepartmentNumber}
                                </Typography>
                            )}
                        </>
                    )}
                </StyledWrapper>
                {cart.fulfillmentMethod === FulfillmentMethod.PickUp && cart.defaultWarehouse && (
                    <StyledWrapper {...styles.pickUpGridItem}>
                        <Typography as="h3" {...styles.pickUpTitleText}>
                            {/* XNG-Change */}
                            {translate("Pick Up Address")}
                        </Typography>
                        <AddressInfoDisplay
                            {...cart.defaultWarehouse}
                            companyName={cart.defaultWarehouse?.description || cart.defaultWarehouse?.name}
                            extendedStyles={styles.pickUpAddress}
                        />
                    </StyledWrapper>
                )}
                {/* XNG-Change: display notes */}
                {cart.notes && (
                    <StyledWrapper {...styles.notesWrapper}>
                        <Typography {...styles.notesTitle}>{translate("Order Notes")}</Typography>
                        <Typography {...styles.notesDescription}>{cart.notes}</Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationBillingAndShipping),
    definition: {
        displayName: "Billing And Shipping",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
